<template>
  <div id="page-tasks-boards" :class="{ 'is-embedded': embedded }">
    <div class="board-card">
      <div class="board-card-header" v-if="!embedded">
        <div class="board-heading">
          <router-link
            class="board-back-btn"
            to="/tasks?view=board"
            :aria-label="t('tasks-back')"
          >
            <Icon icon-class="ui-arrow-left" size="5" />
          </router-link>
          <div class="board-heading-text">
            <h1>{{ board.name || t("tasks-boards-board_title") }}</h1>
          </div>
        </div>
        <div class="board-card-header-actions">
          <kv-button
            variant="filled"
            size="small"
            prepend-icon="ui-plus"
            to="/tasks?create=1"
            :aria-label="t('tasks-add_new_task')"
          >
            <span class="board-label-full">{{ t("tasks-add_new_task") }}</span>
            <span class="board-label-short">{{ t("tasks-add_task_short") }}</span>
          </kv-button>
        </div>
      </div>

      <div class="board-card-filters" v-if="filtersReady">
        <kv-filter
          ref="filterBar"
          :has-period-filter="false"
          :has-branch-filter="canFilterPerBranch"
          :has-client-filter="hasClientFilter"
          :updateQueryString="!embedded"
          :filters="extraFilters"
          @changed="setFilters"
        />
      </div>

      <div class="board-card-body">
        <loader v-if="!ready || loading" />

        <draggable
          v-else
          :list="board.columns"
          group="columns"
          @change="reorderColumns"
          class="kanban-board"
          :options="{ handle: '.column-drag-handle' }"
        >
          <div
            class="kanban-column"
            v-for="column in board.columns"
            :key="column.id"
          >
            <div class="kanban-column-header column-drag-handle">
              <div class="kanban-column-title">
                <Icon
                  icon-class="ui-dots-grid"
                  size="3"
                  class="kanban-drag-icon"
                />
                <input
                  type="text"
                  class="kanban-column-name"
                  :value="column.name"
                  @keyup.enter="renameColumn($event, column)"
                  @blur="renameColumn($event, column)"
                />
              </div>
              <div class="kanban-column-actions">
                <kv-chip size="small" density="extra-compact" rounded="pill">
                  {{ (cards[column.id] || []).length }}
                </kv-chip>
                <button
                  type="button"
                  class="kanban-icon-btn"
                  :title="t('tasks-add')"
                  @click="focusAddInput(column.id)"
                >
                  <Icon icon-class="ui-plus" size="3" />
                </button>
              </div>
            </div>

            <div
              class="kanban-quotation"
              v-show="
                getQuotationValue(column.id, ['all']) > 0 &&
                $module('quotations')
              "
            >
              <small>
                <strong>{{ t("tasks-boards-quotation_total") }}</strong>
                {{ currencySymbol }}
                {{ getQuotationValue(column.id, ["all"]) }}
              </small>
              <div class="kanban-quotation-tip">
                <small class="d-block">
                  <strong>{{ t("tasks-boards-quotation_drafts") }}</strong>
                  {{ currencySymbol }}
                  {{ getQuotationValue(column.id, ["draft"]) }}
                </small>
                <small class="d-block">
                  <strong>{{ t("tasks-boards-quotation_sent") }}</strong>
                  {{ currencySymbol }}
                  {{ getQuotationValue(column.id, ["sent"]) }}
                </small>
                <small class="d-block">
                  <strong>{{ t("tasks-boards-quotation_accepted") }}</strong>
                  {{ currencySymbol }}
                  {{ getQuotationValue(column.id, ["accepted"]) }}
                </small>
                <small class="d-block">
                  <strong>{{ t("tasks-boards-quotation_rejected") }}</strong>
                  {{ currencySymbol }}
                  {{ getQuotationValue(column.id, ["rejected"]) }}
                </small>
                <small class="d-block">
                  <strong>{{ t("tasks-boards-quotation_expired") }}</strong>
                  {{ currencySymbol }}
                  {{ getQuotationValue(column.id, ["expired"]) }}
                </small>
              </div>
            </div>

            <div class="kanban-cards">
              <draggable
                :list="cards[column.id]"
                group="cards"
                @change="move($event, column)"
                class="kanban-drag-area"
                :options="{
                  filter: '.kanban-interactive',
                  // false: still blocks drag from controls, but allows clicks/popups
                  preventOnFilter: false,
                  delay: 120,
                  delayOnTouchOnly: true,
                }"
              >
                <div
                  class="kanban-card"
                  v-for="task in cards[column.id]"
                  :key="task.id"
                  :class="{
                    done: !!task.completed_at || Number(task.archived) === 1,
                  }"
                  @click="onCardClick(task)"
                >
                  <div class="kanban-card-top">
                    <div class="kanban-card-client">
                      <Icon icon-class="ui-user-01" size="2" />
                      <span>{{ clientLabel(task) || "—" }}</span>
                    </div>
                    <kv-chip
                      v-if="task.completed_at"
                      size="small"
                      density="extra-compact"
                      rounded="pill"
                      color="success"
                    >
                      {{ t("tasks-status_completed") }}
                    </kv-chip>
                    <kv-chip
                      v-else-if="Number(task.archived) === 1"
                      size="small"
                      density="extra-compact"
                      rounded="pill"
                      color="muted"
                    >
                      {{ t("tasks-status_archived") }}
                    </kv-chip>
                  </div>

                  <div class="kanban-card-title">{{ task.title }}</div>
                  <div
                    v-if="task.assignee_name || requesterName(task)"
                    class="kanban-card-people"
                  >
                    <span v-if="task.assignee_name">
                      {{ t("tasks-assignee") }}: {{ task.assignee_name }}
                    </span>
                    <span v-if="requesterName(task)">
                      {{ t("tasks-requester") }}: {{ requesterName(task) }}
                    </span>
                  </div>

                  <div class="kanban-card-meta kanban-interactive" @click.stop>
                    <kv-popup
                      placement="bottom right"
                      min-width="220px"
                      max-width="280px"
                    >
                      <template #trigger="{ toggle }">
                        <button
                          type="button"
                          class="kanban-avatar-btn"
                          :title="
                            task.assignee_name || t('tasks-col_assignee')
                          "
                          :disabled="savingId === task.id"
                          @click.stop="toggle"
                        >
                          <span
                            class="kanban-avatar"
                            :class="{ empty: !task.assignee_name }"
                          >
                            <template v-if="task.assignee_name">
                              {{ initials(task.assignee_name) }}
                            </template>
                            <Icon v-else icon-class="ui-user-01" size="2" />
                          </span>
                        </button>
                      </template>
                      <template #content="{ toggle }">
                        <div class="kanban-hot-menu">
                          <button
                            type="button"
                            class="kanban-hot-item"
                            @click.stop.prevent="
                              setAssignee(task, null, toggle)
                            "
                          >
                            {{ t("tasks-none") }}
                          </button>
                          <button
                            v-for="user in canAssignTo"
                            :key="user.id"
                            type="button"
                            class="kanban-hot-item"
                            :class="{
                              active:
                                String(user.id) === String(task.assignee_id),
                            }"
                            @click.stop.prevent="
                              setAssignee(task, user, toggle)
                            "
                          >
                            <span class="kanban-avatar mini">{{
                              initials(user.name)
                            }}</span>
                            <span>{{ user.name }}</span>
                          </button>
                        </div>
                      </template>
                    </kv-popup>

                    <div class="kanban-card-tags" @click.stop>
                      <answer-tags
                        compact
                        display="dots"
                        :tags="task.tags || []"
                        :suggestions="placeTags"
                        :busy="tagBusyId === task.id"
                        @apply="applyListTag(task, $event)"
                        @remove="removeListTag(task, $event)"
                        @update="updateTagDefinition"
                        @delete="deleteTagDefinition"
                      />
                    </div>

                    <kv-popup placement="bottom right" min-width="180px">
                      <template #trigger="{ toggle }">
                        <button
                          type="button"
                          class="kanban-flag"
                          :class="'tone-' + priorityTone(task)"
                          :title="priorityLabel(task)"
                          :disabled="savingId === task.id"
                          @click.stop="toggle"
                        >
                          <Icon icon-class="ui-flag-01" size="2" />
                        </button>
                      </template>
                      <template #content="{ toggle }">
                        <div class="kanban-hot-menu">
                          <button
                            v-for="opt in priorityOptions"
                            :key="opt.value"
                            type="button"
                            class="kanban-hot-item"
                            :class="'tone-' + opt.tone"
                            @click.stop.prevent="
                              setPriority(task, opt.value, toggle)
                            "
                          >
                            <Icon
                              :icon-class="
                                opt.value === 0 ? 'ui-x-close' : 'ui-flag-01'
                              "
                              size="3"
                            />
                            <span>{{ opt.label }}</span>
                          </button>
                        </div>
                      </template>
                    </kv-popup>

                    <div class="kanban-due-wrap" @click.stop>
                      <kv-date-picker
                        :value="dueDateInput(task) || null"
                        value-format="yyyy-MM-dd"
                        size="small"
                        variant="tonal"
                        placement="bottom right"
                        :placeholder="t('tasks-no_date')"
                        :disabled="savingId === task.id"
                        :clearable="true"
                        @input="setDueDate(task, $event || null)"
                      >
                        <template #trigger>
                          <button
                            type="button"
                            class="task-due-pill"
                            :class="{
                              empty: !dueDateInput(task),
                              late: isLateClass(task) === 'late',
                            }"
                            :disabled="savingId === task.id"
                            :title="t('tasks-col_due_date')"
                          >
                            <Icon icon-class="ui-calendar" size="2" />
                            <human-date
                              v-if="dueDateInput(task)"
                              :date="dueDateInput(task) + 'T12:00:00'"
                              :format="t('dates-format_date')"
                            />
                            <span v-else>{{ t("tasks-no_date") }}</span>
                          </button>
                        </template>
                      </kv-date-picker>
                    </div>

                    <button
                      type="button"
                      class="kanban-complete-btn"
                      :class="{ completed: !!task.completed_at }"
                      :title="
                        task.completed_at
                          ? t('tasks-reopen')
                          : t('tasks-complete')
                      "
                      :disabled="savingId === task.id"
                      @click.stop="toggleComplete(task)"
                    >
                      <Icon icon-class="ui-check" size="2" />
                    </button>
                  </div>
                </div>
              </draggable>

              <div
                v-if="!(cards[column.id] && cards[column.id].length)"
                class="kanban-empty"
              >
                {{ t("tasks-boards-empty_column") }}
              </div>
            </div>

            <div class="kanban-column-footer">
              <input
                :ref="'addInput-' + column.id"
                type="text"
                class="kanban-add-input"
                :placeholder="t('tasks-boards-add_task_placeholder')"
                @keyup.enter="addCard($event, column)"
              />
            </div>
          </div>

          <div class="kanban-add-column">
            <button type="button" class="kanban-add-column-btn" @click="addColumn">
              <Icon icon-class="ui-plus" size="4" />
              <span>{{ t("tasks-boards-new_column") }}</span>
            </button>
          </div>
        </draggable>
      </div>
    </div>

    <drawer
      ref="taskDetails"
      id="task-board-details-drawer"
      size="xl"
      :has-header="false"
      :backdrop="false"
      @hide="onDrawerHide"
    >
      <template #body="{ close }">
        <TaskDetails
          v-if="activeTask && activeTask.id"
          :active-task="activeTask"
          :active-task-id="activeTask.id"
          @closeModal="close"
          @changed="updatedTask"
        />
      </template>
    </drawer>
  </div>
</template>

<style>
.page-content.tasks-boards-id {
  padding: 0;
  max-width: 100%;
  background: #f9fafb;
}

.page-content.tasks-boards-id .loggedAs {
  margin-top: 60px;
}

@media screen and (min-width: 701px) {
  .page-content.tasks-boards-id {
    padding-left: 230px;
    padding-right: 0;
  }

  .page-content.tasks-boards-id .loggedAs {
    margin: 0;
  }
}
</style>

<style scoped lang="scss">
#page-tasks-boards {
  padding: 20px;
  box-sizing: border-box;
  min-height: calc(100vh);
  width: 100%;

  @media screen and (max-width: 767px) {
    padding: 12px;
  }

  &.is-embedded {
    padding: 0;
    min-height: 0;

    .board-card {
      border: 0;
      border-radius: 0;
      min-height: 0;
      background: transparent;
    }

    .board-card-filters {
      padding-left: 0;
      padding-right: 0;
    }

    .board-card-body {
      padding-left: 0;
      padding-right: 0;
    }
  }
}

.board-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: calc(100dvh - 120px);

  @media screen and (max-width: 767px) {
    min-height: calc(100dvh - 90px);
  }
}

.board-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 24px 8px;
  flex-shrink: 0;

  h1 {
    margin: 0;
    font-size: 20px;
    font-weight: 500;
    color: #111927;
  }

  .board-label-short {
    display: none;
  }

  @media screen and (max-width: 767px) {
    padding: 14px 16px 8px;
    gap: 8px;

    h1 {
      font-size: 18px;
    }

    .board-label-full {
      display: none;
    }

    .board-label-short {
      display: inline;
    }
  }
}

.board-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.board-back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  color: #6c737f;
  text-decoration: none;
  flex-shrink: 0;

  &:hover {
    background: #f3f4f6;
    color: #111927;
  }
}

.board-heading-text {
  min-width: 0;

  h1 {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.board-card-header-actions {
  flex-shrink: 0;
}

.board-card-filters {
  padding: 8px 24px 12px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;

  ::v-deep .kv-filters {
    min-height: 0;
    gap: 12px;
  }

  ::v-deep .kv-filter .d-flex.gap-md {
    gap: 12px !important;
  }

  @media screen and (max-width: 767px) {
    padding: 8px 16px 12px;

    ::v-deep .kv-filters {
      gap: 10px;
    }
  }
}

.board-card-body {
  flex: 1;
  min-height: 0;
  padding: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.kanban-board {
  display: flex;
  align-items: stretch;
  gap: 16px;
  width: 100%;
  height: 100%;
  min-height: 60vh;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 8px;
}

.kanban-column {
  flex: 0 0 320px;
  width: 320px;
  max-width: 320px;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  box-sizing: border-box;
}

.kanban-column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.kanban-column-title {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
}

.kanban-drag-icon {
  color: #9da4ae;
  flex-shrink: 0;
}

.kanban-column-name {
  width: 100%;
  min-width: 0;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 8px;
  padding: 4px 6px;
  font-size: 14px;
  font-weight: 500;
  color: #384250;

  &:hover {
    color: #111927;
  }

  &:focus {
    outline: none;
    background: #fff;
    border-color: #e5e7eb;
  }

  &.static {
    border: none;
    padding-left: 0;
  }
}

.kanban-column-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.kanban-icon-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: #9da4ae;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: #edfcf3;
    color: var(--primary, #0b9055);
  }
}

.kanban-quotation {
  position: relative;
  margin: 0 0 8px;
  padding: 0 4px;
  color: #6c737f;
  font-size: 12px;

  &:hover .kanban-quotation-tip {
    opacity: 1;
    visibility: visible;
  }
}

.kanban-quotation-tip {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 5;
  min-width: 180px;
  padding: 10px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;

  small {
    margin: 4px 0;
  }
}

.kanban-cards {
  flex: 1;
  min-height: 150px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.kanban-drag-area {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 40px;
  flex: 1;
}

.kanban-empty {
  padding: 24px 8px;
  text-align: center;
  color: #9da4ae;
  font-size: 13px;
  pointer-events: none;
}

.kanban-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
  cursor: pointer;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    border-color: #d2d6db;
  }

  &.sortable-ghost {
    opacity: 0.5;
  }

  &.done {
    opacity: 0.65;

    .kanban-card-title {
      color: #9da4ae;
      text-decoration: line-through;
    }
  }
}

.kanban-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
  min-width: 0;
}

.kanban-card-client {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  color: #384250;
  font-size: 12px;
  font-weight: 500;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.kanban-card-title {
  font-size: 15px;
  font-weight: 500;
  color: #111927;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 12px;
}

.kanban-card-people {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: -6px 0 10px;
  color: #6c737f;
  font-size: 11px;
  line-height: 1.3;
}

.kanban-card-tags {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
}

.kanban-card-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 12px;
  border-top: 2px dashed #f3f4f6;
}

.kanban-avatar-btn {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
}

.kanban-avatar {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eef2ff;
  color: #3538cd;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;

  &.empty {
    background: #f3f4f6;
    color: #9da4ae;
  }

  &.mini {
    width: 22px;
    height: 22px;
    font-size: 10px;
  }
}

.kanban-flag {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  cursor: pointer;

  &.tone-high {
    color: #f04438;
    background: #fef3f2;
    border-color: transparent;
  }

  &.tone-medium {
    color: #dc6803;
    background: #fffaeb;
    border-color: transparent;
  }

  &.tone-low {
    color: #384250;
    background: #f3f4f6;
    border-color: transparent;
  }

  &.tone-undefined {
    color: #9da4ae;
    border-style: dashed;
    background: transparent;
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
}

.kanban-due-wrap {
  max-width: 200px;
  min-width: 0;

  ::v-deep .kv-date-picker,
  ::v-deep .kv-date-picker-trigger {
    display: inline-flex;
    width: auto;
    max-width: 100%;
  }
}

.task-due-pill {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: #f3f4f6;
  color: #384250;
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  max-width: 100%;

  ::v-deep .human-date {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.empty {
    color: #9da4ae;
    background: #fff;
    border-color: #e5e7eb;
    border-style: dashed;
  }

  &.late {
    color: #f04438;
    background: #fef3f2;
    border-color: transparent;
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
}

.kanban-hot-menu {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 280px;
  overflow: auto;
  min-width: 160px;
}

.kanban-hot-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  background: transparent;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  text-align: left;
  color: #384250;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }

  &.active {
    background: #edfcf3;
    color: #0b9055;
  }

  &.tone-high {
    color: #f04438;
  }
  &.tone-medium {
    color: #dc6803;
  }
  &.tone-low {
    color: #384250;
  }
  &.tone-undefined {
    color: #6c737f;
  }
}

.kanban-complete-btn {
  margin-left: auto;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1.5px dashed #d2d6db;
  background: transparent;
  color: #d2d6db;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    color: #9da4ae;
    border-color: #9da4ae;
  }

  &.completed {
    color: var(--primary, #0b9055);
    border-style: solid;
    border-color: #a6f4c5;
    background: #edfcf3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
}

.kanban-column-footer {
  margin-top: 10px;
  flex-shrink: 0;
}

.kanban-add-input {
  width: 100%;
  border: 1px dashed #e5e7eb;
  background: transparent;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: #384250;

  &::placeholder {
    color: #9da4ae;
  }

  &:focus {
    outline: none;
    background: #fff;
    border-style: solid;
    border-color: #d2d6db;
  }
}

.kanban-add-column {
  flex: 0 0 280px;
  width: 280px;
}

.kanban-add-column-btn {
  width: 100%;
  min-height: 48px;
  border: 1px dashed #d2d6db;
  background: #fff;
  border-radius: 12px;
  color: #6c737f;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 12px;

  &:hover {
    border-color: var(--primary, #0b9055);
    color: var(--primary, #0b9055);
    background: #edfcf3;
  }
}
</style>

<script>
import keviApi from "../../keviApi";
import draggable from "vuedraggable";
import Drawer from "../../components/drawer/drawer.vue";
import TaskDetails from "../task-details";
import { mapState } from "vuex";
import Icon from "../../components/icon/icon";
import KvButton from "../../components/form/kv-button.vue";
import KvChip from "../../components/ui/kv-chip.vue";
import KvPopup from "../../components/ui/kv-popup.vue";
import KvDatePicker from "../../components/inputs/kv-date-picker.vue";
import HumanDate from "../../components/human-date/human-date";
import FilterBar from "../../components/reports/kv-filter-bar.vue";
import AnswerTags from "../../inbox/answer-tags.vue";
import {
  taskAssigneeInitials,
  taskClientName,
  taskPriorityFlagTone,
  taskPriorityKey,
} from "../../helpers/taskDisplay";
require("../task-drawer.less");

export default {
  props: {
    boardId: {
      type: [String, Number],
      default: null,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    TaskDetails,
    Drawer,
    draggable,
    Icon,
    HumanDate,
    "kv-button": KvButton,
    "kv-chip": KvChip,
    "kv-popup": KvPopup,
    "kv-date-picker": KvDatePicker,
    "kv-filter": FilterBar,
    "answer-tags": AnswerTags,
  },
  data: () => ({
    automations: [],
    activeTask: {},
    board: {
      name: "",
      columns: [],
    },
    tasks: [],
    cards: {},
    lastRefresh: null,
    timeout: null,
    ready: false,
    filtersReady: false,
    loading: false,
    filters: {},
    previousRequest: null,
    extraFilters: [],
    savingId: null,
    canAssignTo: [],
    placeTags: [],
    tagBusyId: null,
    ignoreCardClickUntil: 0,
    switchingTask: false,
  }),
  mounted() {
    document.addEventListener("mousedown", this.onDocumentMouseDown, true);
    (async () => {
      await this.loadAutomations();
      const currentQueryStringFilters = JSON.parse(
        this.$route.query.filter || "{}",
      );
      this.filters = null;
      let filters = {};
      if (Object.keys(currentQueryStringFilters).length > 0) {
        filters = currentQueryStringFilters;
      }

      if (this.automations.length > 0) {
        this.extraFilters.push({
          component: "checkbox",
          label: this.t("tasks-filter_automation"),
          name: "automation_id",
          icon: "ui-refresh-ccw-01",
          options: {
            options: this.automations.map((automation) => {
              return { value: automation.id, label: automation.title };
            }),
          },
          removable: true,
        });
      }

      this.extraFilters.push({
        component: "kv-select",
        label: this.t("tasks-filter_status"),
        name: "status",
        icon: "ui-check-circle",
        searchable: false,
        options: {
          options: [
            {
              value: "pending",
              label: this.t("tasks-filter_status_pending"),
            },
            {
              value: "completed",
              label: this.t("tasks-filter_status_completed"),
            },
            {
              value: "archived",
              label: this.t("tasks-filter_status_archived"),
            },
          ],
        },
        removable: false,
      });

      let level = this.$store.state.user.permissions.tasks.options.level;
      if (["all", "branch", "mine"].includes(level)) {
        await this.$store.dispatch("placeDetails/setPlace", this.placeId);
        let users = this.users.filter((u) => parseInt(u.active) === 1);
        let me = users.find((u) => u.username === this.user.username);
        if (level === "mine") {
          this.canAssignTo = me ? [{ ...me }] : [];
        } else {
          if (level === "branch" && me) {
            users = users.filter(
              (u) => u.places_branches_id === me.places_branches_id,
            );
          }
          this.canAssignTo = users;
          this.extraFilters.push({
            component: "kv-select",
            label: this.t("tasks-filter_assignee"),
            name: "assignee",
            icon: "ui-user-square",
            searchable: true,
            options: {
              options: users.map((u) => ({ value: u.id, label: u.name })),
            },
            removable: true,
          });
        }
      }

      this.extraFilters.push({
        component: "period",
        label: this.t("tasks-filter_task_due_date"),
        name: "task-due-date-period",
        icon: "ui-calendar-check-02",
        removable: true,
      });
      this.extraFilters.push({
        component: "period",
        label: this.t("tasks-filter_creation_date"),
        name: "task-created-date-period",
        icon: "ui-calendar-date",
        removable: true,
      });

      await this.loadTagFilter();

      this.filtersReady = true;
      await this.$nextTick();
      const filterBar = this.$refs.filterBar;
      if (filterBar) {
        if (Object.keys(filters).length > 0) {
          filterBar.setValues(filters);
        }
        filterBar.addFilter("status", false);
        if (!["pending", "completed", "archived"].includes(filters?.status)) {
          filterBar.setValue("status", "pending");
        }
      }
      this.ready = true;
    })();
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.onDocumentMouseDown, true);
    if (this.$refs.taskDetails) {
      this.$refs.taskDetails.close();
    }
    window.clearTimeout(this.timeout);
  },
  computed: {
    ...mapState({
      user: (state) => state.user,
    }),
    ...mapState("placeDetails", ["users", "roles", "branches"]),
    resolvedBoardId() {
      if (this.boardId != null && this.boardId !== "") {
        return this.boardId;
      }
      return this.$route.params.id;
    },
    placeId() {
      return this.$store.state.place.id;
    },
    canFilterPerBranch() {
      let inbox = this.user.permissions.inbox;
      return (
        parseInt(inbox.enabled) === 1 &&
        parseInt(inbox.options.global_access) === 1
      );
    },
    hasClientFilter() {
      return true;
    },
    priorityOptions() {
      return [
        {
          value: 1,
          tone: "low",
          label: this.t("tasks-priority_low"),
        },
        {
          value: 2,
          tone: "medium",
          label: this.t("tasks-priority_medium"),
        },
        {
          value: 3,
          tone: "high",
          label: this.t("tasks-priority_high"),
        },
        {
          value: 0,
          tone: "undefined",
          label: this.t("tasks-priority_undefined"),
        },
      ];
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    clientLabel: taskClientName,
    initials: taskAssigneeInitials,
    requesterName(task) {
      if (task.created_by_name) {
        return task.created_by_name;
      }
      const requester = this.canAssignTo.find(
        (user) => String(user.id) === String(task.users_id || ""),
      );
      return requester ? requester.name : "";
    },
    priorityTone(task) {
      return taskPriorityFlagTone(task && task.priority);
    },
    priorityLabel(task) {
      const key = taskPriorityKey(task && task.priority);
      return this.t("tasks-priority_" + key);
    },
    dueDateInput(task) {
      if (!task || !task.due_date) return "";
      return String(task.due_date).slice(0, 10);
    },
    closePopup(toggle) {
      if (typeof toggle === "function") {
        toggle();
      }
    },
    applyLocalChange(task, patch) {
      Object.keys(patch || {}).forEach((key) => {
        this.$set(task, key, patch[key]);
      });
    },
    focusAddInput(columnId) {
      const ref = this.$refs["addInput-" + columnId];
      const input = Array.isArray(ref) ? ref[0] : ref;
      if (input && input.focus) {
        input.focus();
      }
    },
    setFilters(filters) {
      this.filters = filters;
      this.loadBoard();
    },
    async loadAutomations() {
      const options = keviApi.getOptions("tasks/automations", "GET");
      this.automations = (await this.$http(options)).body;
    },
    async loadTagFilter() {
      try {
        const response = await this.$http(keviApi.getOptions("tasks-tags"));
        const tags = (response.body && response.body.tags) || [];
        this.placeTags = tags;
        if (!tags.length) {
          return;
        }
        this.extraFilters.push({
          component: "checkbox",
          name: "tags",
          label: this.t("tasks-filter_tags"),
          options: {
            options: tags.map((tag) => ({
              value: tag.id,
              label: tag.name,
              color: tag.color,
            })),
            icon: "ui-tag-01",
          },
          removable: true,
        });
      } catch (error) {
        console.error("Error loading task tags filter", error);
      }
    },
    rememberCreatedTag(tag) {
      if (!tag || !tag.id) return;
      const exists = this.placeTags.some(
        (item) => String(item.id) === String(tag.id),
      );
      if (!exists) {
        this.placeTags = this.placeTags.concat([tag]);
      }
    },
    patchTaskTags(task, tags) {
      this.$set(task, "tags", tags || []);
      if (this.activeTask && String(this.activeTask.id) === String(task.id)) {
        this.activeTask = { ...this.activeTask, tags: tags || [] };
      }
    },
    applyListTag(task, payload) {
      this.tagBusyId = task.id;
      const options = keviApi.getOptions(
        "task/" + task.id + "/tags",
        "POST",
        payload,
      );
      this.$http(options)
        .then((response) => {
          const body = response.body || {};
          this.patchTaskTags(task, body.tags);
          if (body.tag) {
            this.rememberCreatedTag(body.tag);
          }
        })
        .catch(() => {
          this.$store.dispatch(
            "notification/error",
            this.t("tasks-notify_tag_error"),
          );
        })
        .finally(() => {
          this.tagBusyId = null;
        });
    },
    removeListTag(task, tag) {
      this.tagBusyId = task.id;
      const options = keviApi.getOptions(
        "task/" + task.id + "/tags/" + tag.id,
        "DELETE",
      );
      this.$http(options)
        .then((response) => {
          this.patchTaskTags(task, (response.body && response.body.tags) || []);
        })
        .catch(() => {
          this.$store.dispatch(
            "notification/error",
            this.t("tasks-notify_tag_error"),
          );
        })
        .finally(() => {
          this.tagBusyId = null;
        });
    },
    patchTagAcrossTasks(tag) {
      if (!tag || !tag.id) return;
      this.placeTags = (this.placeTags || []).map((item) =>
        String(item.id) === String(tag.id) ? { ...item, ...tag } : item,
      );
      (this.tasks || []).forEach((task) => {
        const tags = task.tags || [];
        if (!tags.some((item) => String(item.id) === String(tag.id))) {
          return;
        }
        this.patchTaskTags(
          task,
          tags.map((item) =>
            String(item.id) === String(tag.id) ? { ...item, ...tag } : item,
          ),
        );
      });
    },
    removeTagAcrossTasks(tagId) {
      this.placeTags = (this.placeTags || []).filter(
        (item) => String(item.id) !== String(tagId),
      );
      (this.tasks || []).forEach((task) => {
        const tags = task.tags || [];
        if (!tags.some((item) => String(item.id) === String(tagId))) {
          return;
        }
        this.patchTaskTags(
          task,
          tags.filter((item) => String(item.id) !== String(tagId)),
        );
      });
    },
    updateTagDefinition(payload) {
      if (!payload || !payload.id) return;
      this.tagBusyId = payload.id;
      const options = keviApi.getOptions("tasks-tags", "POST", {
        id: payload.id,
        name: payload.name,
        color: payload.color,
      });
      this.$http(options)
        .then((response) => {
          const tag = (response.body && response.body.tag) || payload;
          this.patchTagAcrossTasks(tag);
        })
        .catch(() => {
          this.$store.dispatch(
            "notification/error",
            this.t("tasks-notify_tag_error"),
          );
        })
        .finally(() => {
          this.tagBusyId = null;
        });
    },
    deleteTagDefinition(tag) {
      if (!tag || !tag.id) return;
      this.tagBusyId = tag.id;
      const options = keviApi.getOptions("tasks-tags/" + tag.id, "DELETE");
      this.$http(options)
        .then(() => {
          this.removeTagAcrossTasks(tag.id);
        })
        .catch(() => {
          this.$store.dispatch(
            "notification/error",
            this.t("tasks-notify_tag_error"),
          );
        })
        .finally(() => {
          this.tagBusyId = null;
        });
    },
    isLateClass(task) {
      if (!task.completed_at && task.due_date) {
        let dueDate = new Date(task.due_date);
        dueDate.setHours(0, 0, 0, 0);
        let today = new Date();
        today.setHours(0, 0, 0, 0);
        if (dueDate.getTime() < today.getTime()) {
          return "late";
        }
        if (dueDate.getTime() === today.getTime()) {
          return "text-warning";
        }
      }
      return "";
    },
    async loadBoard(refresh = 0) {
      if (!this.ready || !this.resolvedBoardId) {
        return;
      }
      if (!refresh) {
        this.loading = true;
      }
      const options = keviApi.getOptions(
        "/task-boards/" + this.resolvedBoardId,
        "GET",
        { refreshTime: refresh, filters: this.filters },
      );
      options.before = (request) => {
        if (this.previousRequest) {
          this.previousRequest.abort();
        }
        this.previousRequest = request;
      };
      const response = await this.$http(options);

      this.loading = false;
      this.tasks = response.body;
      this.board.name = response.data.name;
      this.board.columns = response.data.columns;

      const nextCards = {};
      this.board.columns.forEach((column) => {
        nextCards[column.id] = [];
      });
      (response.data.tasks || []).forEach((task) => {
        if (nextCards[task.tasks_boards_columns_id]) {
          nextCards[task.tasks_boards_columns_id].push(task);
        }
      });
      this.cards = nextCards;
      this.lastRefresh = response.data.refreshTime;

      window.clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.loadBoard(this.lastRefresh);
      }, 3000);
    },
    loadActiveTask(task) {
      if (task && task.id) {
        this.switchingTask = true;
        const options = keviApi.getOptions("task/" + task.id, "GET");
        this.$http(options)
          .then((response) => {
            this.activeTask = response.body.task;
            this.activeTask.id = task.id;
            this.$refs.taskDetails.open();
          })
          .finally(() => {
            this.$nextTick(() => {
              this.switchingTask = false;
            });
          });
      } else {
        this.activeTask = null;
      }
    },
    async addColumn() {
      let options = keviApi.getOptions(
        "/task-boards/" + this.resolvedBoardId + "/columns",
        "POST",
      );
      const response = await this.$http(options);
      this.board.columns.push(response.data);
      this.$set(this.cards, response.data.id, []);
    },
    async addCard(event, column) {
      const title = event.target.value;
      if (!title) {
        return;
      }
      let options = keviApi.getOptions(
        "/task-boards/" + this.resolvedBoardId + "/tasks",
        "POST",
        {
          title,
          tasks_boards_columns_id: column.id,
        },
      );
      const response = await this.$http(options);
      if (!this.cards[column.id]) {
        this.$set(this.cards, column.id, []);
      }
      this.cards[column.id].unshift(response.data);
      event.target.value = "";
    },
    async updatePosition(column) {
      this.cards[column.id] = this.cards[column.id].map((task, index) => {
        task.position = index;
        return task;
      });
      let options = keviApi.getOptions(
        "/task-boards/" +
          this.resolvedBoardId +
          "/columns/" +
          column.id +
          "/reorder",
        "POST",
        {
          tasks: this.cards[column.id],
        },
      );
      await this.$http(options);
    },
    async renameColumn(event, column) {
      const newName = event.target.value;
      if (newName === column.name) {
        return;
      }
      if (!newName) {
        this.archiveColumn(column);
      } else {
        let options = keviApi.getOptions(
          "/task-boards/" +
            this.resolvedBoardId +
            "/columns/" +
            column.id,
          "POST",
          {
            name: event.target.value,
          },
        );
        await this.$http(options);
        column.name = newName;
      }
    },
    getQuotationValue(columnId, status) {
      const statusList = status;
      const list = this.cards[columnId] || [];
      const total = list
        .reduce((total, task) => {
          if (
            statusList.includes(task.quotation_status) ||
            statusList.includes("all")
          ) {
            const value = Number(task.quotation_value) || 0;
            return total + value;
          }
          return total + 0;
        }, 0)
        .toFixed(2);
      return total;
    },
    async archiveColumn(column) {
      let options = keviApi.getOptions(
        "/task-boards/" +
          this.resolvedBoardId +
          "/columns/" +
          column.id,
        "DELETE",
      );
      await this.$http(options);
      this.board.columns = this.board.columns.filter((c) => c.id !== column.id);
    },
    async move(event, column) {
      if (event.added) {
        const task = event.added.element;
        let options = keviApi.getOptions(
          "/task-boards/" +
            this.resolvedBoardId +
            "/tasks/" +
            task.id +
            "/move",
          "POST",
          {
            tasks_boards_columns_id: column.id,
            position: event.added.newIndex,
          },
        );
        await this.$http(options);
        this.updatePosition(column);
        task.tasks_boards_columns_id = column.id;
      }
      if (event.moved) {
        this.updatePosition(column);
      }
    },
    async editTaskFields(task, fields = {}) {
      if (!task || this.savingId === task.id) {
        return null;
      }
      this.savingId = task.id;
      try {
        const payload = {
          assignee_id: Object.prototype.hasOwnProperty.call(fields, "assignee_id")
            ? fields.assignee_id || ""
            : task.assignee_id || "",
          clients_id: Object.prototype.hasOwnProperty.call(fields, "clients_id")
            ? fields.clients_id || ""
            : task.clients_id || "",
          due_date: Object.prototype.hasOwnProperty.call(fields, "due_date")
            ? fields.due_date || ""
            : task.due_date
              ? String(task.due_date).slice(0, 10)
              : "",
          priority: Object.prototype.hasOwnProperty.call(fields, "priority")
            ? Number(fields.priority)
            : Number(task.priority || 0),
        };
        const options = keviApi.getOptions(
          `task/${task.id}/edit`,
          "POST",
          payload,
        );
        const response = await this.$http(options);
        const updated =
          (response && response.body && response.body.task) ||
          (response && response.data && response.data.task) ||
          null;
        if (!updated) {
          throw new Error("missing task in edit response");
        }
        return updated;
      } catch (e) {
        this.$store.dispatch(
          "notification/error",
          this.t("tasks-notify_error_update"),
        );
        return null;
      } finally {
        this.savingId = null;
      }
    },
    suppressCardClick() {
      this.ignoreCardClickUntil = Date.now() + 400;
    },
    onCardClick(task) {
      if (Date.now() < this.ignoreCardClickUntil) {
        return;
      }
      this.loadActiveTask(task);
    },
    onDrawerHide() {
      if (this.switchingTask) {
        return;
      }
      this.activeTask = null;
    },
    onDocumentMouseDown(event) {
      const drawer = this.$refs.taskDetails;
      if (!drawer || !drawer.isOpen) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (target.closest(".drawer-panel")) {
        return;
      }
      if (target.closest(".kanban-card")) {
        this.switchingTask = true;
        this.$nextTick(() => {
          this.switchingTask = false;
        });
        return;
      }
      if (target.closest(".kv-popup-content, .kv-dropdown, .kanban-hot-menu")) {
        return;
      }
      drawer.close();
    },
    async setPriority(task, priority, toggle) {
      this.suppressCardClick();
      this.closePopup(toggle);
      if (!task || Number(task.priority) === Number(priority)) {
        return;
      }
      this.applyLocalChange(task, { priority: Number(priority) });
      const updated = await this.editTaskFields(task, { priority });
      if (!updated) {
        return;
      }
      this.applyLocalChange(task, {
        priority: Number(updated.priority),
      });
    },
    async setAssignee(task, user, toggle) {
      this.suppressCardClick();
      this.closePopup(toggle);
      const nextId = user ? user.id : null;
      if (String(task.assignee_id || "") === String(nextId || "")) {
        return;
      }
      this.applyLocalChange(task, {
        assignee_id: nextId,
        assignee_name: user ? user.name : null,
      });
      const updated = await this.editTaskFields(task, {
        assignee_id: nextId || "",
      });
      if (!updated) {
        return;
      }
      this.applyLocalChange(task, {
        assignee_id: updated.assignee_id || nextId,
        assignee_name:
          updated.assignee_name || (user ? user.name : null),
      });
    },
    async setDueDate(task, value) {
      const next =
        value == null || value === "" ? null : String(value).slice(0, 10);
      const current = task.due_date ? String(task.due_date).slice(0, 10) : null;
      if (next === current) {
        return;
      }
      this.applyLocalChange(task, { due_date: next });
      const updated = await this.editTaskFields(task, {
        due_date: next || "",
      });
      if (!updated) {
        return;
      }
      this.applyLocalChange(task, {
        due_date: updated.due_date
          ? String(updated.due_date).slice(0, 10)
          : next,
      });
    },
    async toggleComplete(task) {
      if (!task || this.savingId === task.id) {
        return;
      }
      this.savingId = task.id;
      const action = task.completed_at ? "uncomplete" : "complete";
      try {
        const options = keviApi.getOptions(`task/${task.id}/${action}`, "POST");
        const response = await this.$http(options);
        const updated =
          (response.body && response.body.task) ||
          (response.data && response.data.task) ||
          {};
        this.applyLocalChange(task, {
          completed_at: updated.completed_at || null,
        });
        if (action === "complete") {
          this.$store.dispatch("notification/success", {
            text: this.t("tasks-notify_task_completed"),
            actionLabel: this.t("undo"),
            onAction: () =>
              this.toggleComplete({
                ...task,
                completed_at:
                  updated.completed_at || new Date().toISOString(),
              }),
          });
        } else {
          this.$store.dispatch(
            "notification/success",
            this.t("tasks-notify_task_reopened"),
          );
        }
      } catch (e) {
        this.$store.dispatch(
          "notification/error",
          this.t("tasks-notify_error_update"),
        );
      } finally {
        this.savingId = null;
      }
    },
    async reorderColumns() {
      const positionMap = this.board.columns.map((column, index) => {
        return {
          id: column.id,
          position: index,
        };
      });
      let options = keviApi.getOptions(
        "/task-boards/" + this.resolvedBoardId + "/columns/reorder",
        "POST",
        {
          columns: positionMap,
        },
      );
      await this.$http(options);
    },
    updatedTask({ id, task } = {}) {
      this.loadBoard();
      if (
        task &&
        this.activeTask &&
        String(id) === String(this.activeTask.id)
      ) {
        this.activeTask = { ...this.activeTask, ...task };
        return;
      }
      if (this.activeTask && this.activeTask.id) {
        this.loadActiveTask(this.activeTask);
      }
    },
  },
  watch: {
    ready() {
      if (this.ready) {
        this.loadBoard();
      }
    },
    resolvedBoardId(next, prev) {
      if (!this.ready || !next || String(next) === String(prev)) {
        return;
      }
      window.clearTimeout(this.timeout);
      this.loadBoard();
    },
  },
};
</script>
